import { useState } from 'react'
import { Confirm } from 'notiflix'
import styles from './styles.module.scss'
import {
    INTRODUCER_INDUSTRIES,
    IntroducerApp,
    IntroducerIndustry,
    useIntroducerRegistry,
} from './useIntroducerRegistry'

type FormState = Omit<IntroducerApp, 'id' | 'industry'> & {
    industry: IntroducerIndustry | ''
}

const EMPTY_FORM: FormState = {
    display_name: '',
    aws_app_name: '',
    repo: '',
    live_domain: '',
    branch_name: 'main',
    github_url: '',
    industry: '',
    active: true,
}

const prettyDomain = (domain: string) => domain.replace(/^https?:\/\//, '').replace(/\/$/, '')

const repoOwner = (githubUrl: string) => {
    const [owner] = (githubUrl || '').replace(/^https?:\/\/github\.com\//, '').split('/')
    return owner || 'github'
}

const toFormState = (app: IntroducerApp): FormState => ({
    display_name: app.display_name,
    aws_app_name: app.aws_app_name,
    repo: app.repo,
    live_domain: app.live_domain,
    branch_name: app.branch_name,
    github_url: app.github_url,
    industry: app.industry,
    active: app.active,
})

export const IntroducersDirectoryView = () => {
    const { apps: introducerApps, isLoading, createApp, updateApp, deleteApp } = useIntroducerRegistry()
    const [search, setSearch] = useState('')

    const [modalOpen, setModalOpen] = useState(false)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [form, setForm] = useState<FormState>(EMPTY_FORM)
    const [formError, setFormError] = useState<string | null>(null)
    const [saving, setSaving] = useState(false)

    const q = search.toLowerCase()
    const filtered = introducerApps.filter(
        (a) =>
            a.display_name.toLowerCase().includes(q) ||
            a.aws_app_name.toLowerCase().includes(q) ||
            a.repo.toLowerCase().includes(q) ||
            a.live_domain.toLowerCase().includes(q) ||
            a.branch_name.toLowerCase().includes(q) ||
            a.industry.toLowerCase().includes(q),
    )

    const openAddModal = () => {
        setEditingId(null)
        setForm(EMPTY_FORM)
        setFormError(null)
        setModalOpen(true)
    }

    const openEditModal = (app: IntroducerApp) => {
        setEditingId(app.id)
        setForm(toFormState(app))
        setFormError(null)
        setModalOpen(true)
    }

    const closeModal = () => {
        setModalOpen(false)
        setFormError(null)
    }

    const handleFieldChange = (field: keyof FormState, value: string) => {
        setForm((prev) => ({ ...prev, [field]: value }))
    }

    const handleSave = async () => {
        if (!form.display_name.trim()) {
            setFormError('Display name is required')
            return
        }
        if (!form.live_domain.trim()) {
            setFormError('Live domain is required')
            return
        }
        if (!form.repo.trim()) {
            setFormError('Github repo is required')
            return
        }
        if (!form.industry) {
            setFormError('Industry is required')
            return
        }

        setSaving(true)
        const payload: Omit<IntroducerApp, 'id'> = {
            ...form,
            industry: form.industry,
        }
        let success: boolean

        if (editingId !== null) {
            const original = introducerApps.find((app) => app.id === editingId)
            const changes = Object.entries(payload).reduce<Partial<Omit<IntroducerApp, 'id'>>>(
                (updatedFields, [field, value]) => {
                    const key = field as keyof Omit<IntroducerApp, 'id'>
                    if (original?.[key] !== value) {
                        return { ...updatedFields, [key]: value }
                    }
                    return updatedFields
                },
                {},
            )

            if (Object.keys(changes).length === 0) {
                setSaving(false)
                setModalOpen(false)
                return
            }

            success = await updateApp({ id: editingId, ...changes })
        } else {
            success = await createApp(payload)
        }
        setSaving(false)

        if (success) {
            setModalOpen(false)
            setFormError(null)
        }
    }

    const handleDelete = (app: IntroducerApp) => {
        Confirm.show(
            'Remove Introducer App?',
            `Are you sure you want to remove "${app.display_name}" from the registry?`,
            'Remove',
            'Cancel',
            () => {
                void deleteApp(app.id)
            },
        )
    }

    return (
        <div className={styles.wrapper}>
            <div className={styles.toolbar}>
                <input
                    className={styles.searchInput}
                    placeholder="Search by app, repo, live domain, branch name or industry…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <div className={styles.toolbarRight}>
                    <span className={styles.resultsCount}>
                        {filtered.length} of {introducerApps.length} apps
                    </span>
                    <button className={styles.primaryButton} onClick={openAddModal}>
                        + Add New
                    </button>
                </div>
            </div>

            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Introducer</th>
                            <th>Deployment</th>
                            <th>Repository</th>
                            <th>Industry</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan={5} className={styles.emptyRow}>
                                    Loading introducer registry…
                                </td>
                            </tr>
                        ) : filtered.length === 0 ? (
                            <tr>
                                <td colSpan={5} className={styles.emptyRow}>
                                    {introducerApps.length === 0
                                        ? 'No apps yet. Click "+ Add New" to create one.'
                                        : 'No apps match your search.'}
                                </td>
                            </tr>
                        ) : (
                            filtered.map((a) => (
                                <tr key={a.id}>
                                    <td>
                                        <div className={styles.stackedCell}>
                                            <span className={styles.primaryText}>{a.display_name}</span>
                                            {a.live_domain ? (
                                                <a
                                                    className={styles.secondaryLink}
                                                    href={a.live_domain}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    {prettyDomain(a.live_domain)}
                                                </a>
                                            ) : (
                                                <span className={styles.secondaryText}>No live domain</span>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <div className={styles.stackedCell}>
                                            <span className={styles.primaryText}>{a.aws_app_name || '—'}</span>
                                            {a.branch_name && (
                                                <span className={styles.branchPill}>{a.branch_name}</span>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <div className={styles.stackedCell}>
                                            <a
                                                className={styles.primaryLink}
                                                href={a.github_url}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                {a.repo}
                                            </a>
                                            <span className={styles.secondaryText}>{repoOwner(a.github_url)}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className={styles.stackedCell}>
                                            <span className={styles.primaryText}>{a.industry || '—'}</span>
                                            <span
                                                className={`${styles.statusPill} ${
                                                    a.active ? styles.statusActive : styles.statusInactive
                                                }`}
                                            >
                                                {a.active ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className={styles.rowActions}>
                                            <button className={styles.subtleButton} onClick={() => openEditModal(a)}>
                                                Edit
                                            </button>
                                            <button
                                                className={`${styles.subtleButton} ${styles.dangerButton}`}
                                                onClick={() => handleDelete(a)}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {modalOpen && (
                <div className={styles.modalOverlay} onClick={closeModal}>
                    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3>{editingId ? 'Edit Introducer App' : 'Add Introducer App'}</h3>
                            <button className={styles.closeButton} onClick={closeModal}>
                                ×
                            </button>
                        </div>

                        <div className={styles.modalBody}>
                            <label className={styles.field}>
                                <span>
                                    Display Name<span className={styles.req}>*</span>
                                </span>
                                <input
                                    value={form.display_name}
                                    onChange={(e) => handleFieldChange('display_name', e.target.value)}
                                    placeholder="e.g. Call Sentinel (PCP)"
                                />
                            </label>
                            <label className={styles.field}>
                                <span>
                                    AWS App Name<span className={styles.req}>*</span>
                                </span>
                                <input
                                    value={form.aws_app_name}
                                    onChange={(e) => handleFieldChange('aws_app_name', e.target.value)}
                                    placeholder="e.g. pcp-webform-sentinel"
                                />
                            </label>
                            <label className={styles.field}>
                                <span>
                                    Github Repo<span className={styles.req}>*</span>
                                </span>
                                <input
                                    value={form.repo}
                                    onChange={(e) => handleFieldChange('repo', e.target.value)}
                                    placeholder="e.g. pcp-webform-sentinel"
                                />
                            </label>
                            <label className={styles.field}>
                                <span>
                                    Github URL<span className={styles.req}>*</span>
                                </span>
                                <input
                                    value={form.github_url}
                                    onChange={(e) => handleFieldChange('github_url', e.target.value)}
                                    placeholder="https://github.com/sentinel-legal/..."
                                />
                            </label>
                            <label className={styles.field}>
                                <span>
                                    Live Domain<span className={styles.req}>*</span>
                                </span>
                                <input
                                    value={form.live_domain}
                                    onChange={(e) => handleFieldChange('live_domain', e.target.value)}
                                    placeholder="https://example.sentinellegal.co.uk"
                                />
                            </label>
                            <label className={styles.field}>
                                <span>Branch Name</span>
                                <input
                                    value={form.branch_name}
                                    onChange={(e) => handleFieldChange('branch_name', e.target.value)}
                                    placeholder="main"
                                />
                            </label>
                            <label className={styles.field}>
                                <span>
                                    Industry<span className={styles.req}>*</span>
                                </span>
                                <select
                                    value={form.industry}
                                    onChange={(e) => handleFieldChange('industry', e.target.value)}
                                >
                                    <option value="">Select industry</option>
                                    {INTRODUCER_INDUSTRIES.map((industry) => (
                                        <option key={industry} value={industry}>
                                            {industry}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className={styles.checkboxField}>
                                <input
                                    type="checkbox"
                                    checked={form.active}
                                    onChange={(e) => setForm((prev) => ({ ...prev, active: e.target.checked }))}
                                />
                                <span>
                                    Active<span className={styles.req}>*</span>
                                </span>
                            </label>
                            {formError && <p className={styles.formError}>{formError}</p>}
                        </div>

                        <div className={styles.modalFooter}>
                            <button className={styles.subtleButton} onClick={closeModal}>
                                Cancel
                            </button>
                            <button className={styles.primaryButton} onClick={handleSave} disabled={saving}>
                                {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Add App'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
